"use client";

import { DimensionDefinition, MultiAssetDimensionItem } from "./type";

import type { ChatStructuredAsset } from "../StructuredCards/types";
import { ChatStructuredDataTable } from "../StructuredCards";
import { ScoreRankCell } from "../StructuredCards/common/ScoreRankCell";
import { buildJsonRenderAssetDetailPath } from "./buildJsonRenderAssetDetailPath";
import { useCallback } from "react";
import { useShellRouter } from "@hooks/useShellAwareRouter";

export interface MultiAssetDimensionScoreComparisonTableProps {
  dimensions: DimensionDefinition[];
  assets: MultiAssetDimensionItem[];
  sectionLabel?: string;
}

interface MultiAssetDimensionScoreComparisonRow {
  asset: ChatStructuredAsset;
  scores: Record<string, { score: number; rankLabel: string }>;
}

export const MultiAssetDimensionScoreComparisonTable: React.FC<
  MultiAssetDimensionScoreComparisonTableProps
> = ({ dimensions, assets, sectionLabel }) => {
  const router = useShellRouter();

  const headerDefinitions =
    assets.length > 0
      ? [{ dimensionKey: "asset", dimensionName: "Asset" }, ...dimensions]
      : dimensions;

  const h = Object.fromEntries(
    headerDefinitions.map(({ dimensionKey, dimensionName }) => [
      dimensionKey,
      dimensionName,
    ]),
  ) as Record<string, string>;

  const rows: MultiAssetDimensionScoreComparisonRow[] = assets.map((asset) => ({
    asset: {
      ...(asset.id !== undefined && asset.id !== "" ? { id: asset.id } : {}),
      imageUrl: asset.assetLogo ?? "",
      symbol: asset.assetSymbol,
      name: asset.assetName,
      schemaAssetType: asset.type,
    },
    scores: Object.fromEntries(
      asset.dimensionScores.map((score) => [
        score.dimensionKey,
        {
          score: score.scoreValue,
          rankLabel: `${score.rankValue}/${score.rankTotalAssets}`,
        },
      ]),
    ),
  }));

  const columns = [
    { key: "asset", header: h.asset, render: () => null },
    ...dimensions.map((dimension) => ({
      key: dimension.dimensionKey,
      header: h[dimension.dimensionKey],
      render: (row: MultiAssetDimensionScoreComparisonRow) => {
        const score = row.scores[dimension.dimensionKey];

        return score ? (
          <ScoreRankCell score={score.score} rankLabel={score.rankLabel} />
        ) : null;
      },
    })),
  ];

  const onRowClick = useCallback(
    (row: MultiAssetDimensionScoreComparisonRow) => {
      const { asset } = row;
      if (!asset.schemaAssetType || !asset.symbol) {
        return;
      }
      router.push(
        buildJsonRenderAssetDetailPath(
          asset.schemaAssetType,
          asset.symbol,
          asset.id,
        ),
      );
    },
    [router],
  );

  return (
    <div className="flex flex-col">
      {sectionLabel && (
        <p className="body_medium_m text-content_primary">{sectionLabel}</p>
      )}
      <ChatStructuredDataTable<MultiAssetDimensionScoreComparisonRow>
        columns={columns}
        rows={rows}
        getAsset={(r) => r.asset}
        assetCompact
        onRowClick={onRowClick}
      />
    </div>
  );
};
